import React, { useState, useEffect } from 'react';
import { X, Globe, Flag } from 'lucide-react';
import { Button, Input } from '../shared';

const CreateCountryModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  country = null, 
  isEdit = false, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    emoji: '',
    unicode: '',
    dial_code: '',
    is_active: true,
    sort_order: 0
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && country) {
      setFormData({
        name: country.name || '',
        code: country.code || '',
        emoji: country.emoji || '',
        unicode: country.unicode || '',
        dial_code: country.dial_code || '',
        is_active: country.is_active !== undefined ? country.is_active : true,
        sort_order: country.sort_order || 0
      });
    } else {
      setFormData({
        name: '',
        code: '',
        emoji: '',
        unicode: '',
        dial_code: '',
        is_active: true,
        sort_order: 0
      });
    }
    setErrors({});
  }, [isEdit, country, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Country name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Country code is required';
    } else if (formData.code.length !== 2) {
      newErrors.code = 'Country code must be 2 characters';
    }

    if (formData.dial_code && !/^\+?\d+$/.test(formData.dial_code)) {
      newErrors.dial_code = 'Dial code must be numeric with optional + prefix';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      emoji: '',
      unicode: '',
      dial_code: '',
      is_active: true,
      sort_order: 0
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
              {isEdit ? (
                <Flag className="h-4 w-4 text-white" />
              ) : (
                <Globe className="h-4 w-4 text-white" />
              )}
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">
              {isEdit ? 'Edit Country' : 'Add New Country'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Country Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Country Name *
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter country name"
              className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Country Code */}
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Country Code *
            </label>
            <Input
              id="code"
              name="code"
              type="text"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., US, GB, CA"
              className={errors.code ? 'border-red-300 focus:border-red-500' : ''}
              maxLength={2}
            />
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
          </div>

          {/* Emoji */}
          <div>
            <label htmlFor="emoji" className="block text-sm font-medium text-gray-700 mb-1">
              Emoji
            </label>
            <Input
              id="emoji"
              name="emoji"
              type="text"
              value={formData.emoji}
              onChange={handleChange}
              placeholder="e.g., 🇺🇸, 🇬🇧, 🇨🇦"
              maxLength={4}
            />
          </div>

          {/* Unicode */}
          <div>
            <label htmlFor="unicode" className="block text-sm font-medium text-gray-700 mb-1">
              Unicode
            </label>
            <Input
              id="unicode"
              name="unicode"
              type="text"
              value={formData.unicode}
              onChange={handleChange}
              placeholder="e.g., U+1F1FA U+1F1F8"
            />
          </div>

          {/* Dial Code */}
          <div>
            <label htmlFor="dial_code" className="block text-sm font-medium text-gray-700 mb-1">
              Dial Code
            </label>
            <Input
              id="dial_code"
              name="dial_code"
              type="text"
              value={formData.dial_code}
              onChange={handleChange}
              placeholder="e.g., +1, +44, +33"
              className={errors.dial_code ? 'border-red-300 focus:border-red-500' : ''}
            />
            {errors.dial_code && (
              <p className="mt-1 text-sm text-red-600">{errors.dial_code}</p>
            )}
          </div>

          {/* Sort Order */}
          <div>
            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <Input
              id="sort_order"
              name="sort_order"
              type="number"
              value={formData.sort_order}
              onChange={handleChange}
              placeholder="0"
              min="0"
              className={errors.sort_order ? 'border-red-300 focus:border-red-500' : ''}
            />
            {errors.sort_order && (
              <p className="mt-1 text-sm text-red-600">{errors.sort_order}</p>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEdit ? 'Update Country' : 'Create Country'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCountryModal;

